import { useState, useRef } from 'react';
import { Upload, ScanText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { isAxiosError } from 'axios';
import api from '../lib/api';

interface OcrPageResult {
  text: string;
  confidence: number;
  method: string;
  parsed?: {
    questionText: string;
    options: string[];
    detectedLanguage: 'ko' | 'mixed';
  };
  requiresManualReview?: boolean;
}

export default function OCRPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<OcrPageResult | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadFile = (nextFile: File) => {
    setFile(nextFile);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(nextFile);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    loadFile(f);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const imageItem = Array.from(e.clipboardData.items).find((item) =>
      item.type.startsWith('image/'),
    );
    if (!imageItem) return;

    const blob = imageItem.getAsFile();
    if (!blob) return;

    e.preventDefault();

    const extension = blob.type.split('/')[1] || 'png';
    const pastedFile = new File([blob], `pasted-image-${Date.now()}.${extension}`, {
      type: blob.type || 'image/png',
    });
    loadFile(pastedFile);
    toast.success('Image pasted successfully');
  };

  const handleExtract = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/ocr/extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data.data);
      toast.success('Text extracted successfully');
    } catch (err) {
      toast.error(
        isAxiosError(err)
          ? err.response?.data?.error?.message || err.message
          : 'OCR extraction failed',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">OCR Pipeline</h1>
        <p className="text-gray-500 mt-1">Extract Korean text from exam images</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Upload Image</h2>

          <div
            onClick={() => inputRef.current?.click()}
            onPaste={handlePaste}
            tabIndex={0}
            role="button"
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary-400 focus:border-primary-400 focus:outline-none transition-colors"
          >
            {preview ? (
              <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
            ) : (
              <div className="space-y-2">
                <Upload size={40} className="mx-auto text-gray-400" />
                <p className="text-gray-500">Click to upload an image</p>
                <p className="text-xs text-gray-400">PNG, JPG, WebP up to 10MB</p>
                <p className="text-xs text-primary-500">
                  Or focus here and paste an image from your clipboard
                </p>
              </div>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />

          <button
            onClick={handleExtract}
            disabled={!file || loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ScanText size={16} />}
            {loading ? 'Extracting...' : 'Extract Text'}
          </button>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Extracted Text</h2>

          {result ? (
            <>
              <div className="flex items-center gap-4 text-sm">
                <span className="badge bg-primary-50 text-primary-700">
                  Method: {result.method}
                </span>
                <span className="badge bg-success-50 text-success-700">
                  Confidence: {(result.confidence * 100).toFixed(1)}%
                </span>
                {result.parsed?.detectedLanguage ? (
                  <span className="badge bg-blue-50 text-blue-700">
                    Language: {result.parsed.detectedLanguage}
                  </span>
                ) : null}
                {result.requiresManualReview ? (
                  <span className="badge bg-amber-50 text-amber-700">Needs review</span>
                ) : null}
              </div>
              {result.parsed ? (
                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Parsed Question</p>
                    <p className="font-korean text-base text-gray-900 whitespace-pre-wrap">
                      {result.parsed.questionText || '—'}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg px-3 py-2 text-sm font-korean text-gray-800"
                      >
                        <span className="text-gray-400 mr-1">{'①②③④'[index]}</span>
                        <span>{result.parsed?.options[index] || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="bg-gray-50 rounded-lg p-4 font-korean text-lg leading-relaxed whitespace-pre-wrap min-h-[200px]">
                {result.text}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(result.text);
                  toast.success('Copied!');
                }}
                className="btn-secondary text-sm"
              >
                Copy to Clipboard
              </button>
            </>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-400 min-h-[200px] flex items-center justify-center">
              Upload an image and click Extract to see results
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
